import type { TransportItem } from "@grafana/faro-core";

const REDACTED_CONTEXT_KEYS = new Set(["data", "body", "requestBody"]);

const STRIPPED_USER_KEYS = new Set(["email", "fullName", "username"]);

const EMAIL_PATTERN = /\b[\w._%+-]+(?:@|%40)[\w.-]+\.[A-Za-z]{2,}\b/g;

const REDACTED_QUERY_PARAM_PATTERN = /([?&])(searchQuery)=[^&#]*/gi;

const PDF_SIGNAL_PATTERN =
  /react-pdf|pdfjs|pdf\.worker|PDFViewer|PDFPage|\bDocument\b|\bPage\b/i;

const NOISE_PATTERNS: RegExp[] = [
  /chrome-extension:\/\//i,
  /\b(?:Talisman|MetaMask)\b/i,
  /window\.ethereum/i,
  /mainWorldSdk/i,
  /has not been configured yet/i,
  /importScripts.*Unexpected token 'export'/i,
  /Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+/i,
  /ResizeObserver loop (?:completed with undelivered notifications|limit exceeded)/i,
];

const TRANSLATE_REMOVECHILD_PATTERN =
  /(?:removeChild|insertBefore).*not a child of this node|not a child of this node/i;

const collectStrings = (value: unknown, acc: string[], depth = 0): void => {
  if (depth > 6 || acc.length > 200) return;

  if (typeof value === "string") {
    acc.push(value);
  } else if (Array.isArray(value)) {
    value.forEach((v) => collectStrings(v, acc, depth + 1));
  } else if (value !== null && typeof value === "object") {
    Object.values(value as Record<string, unknown>).forEach((v) =>
      collectStrings(v, acc, depth + 1),
    );
  }
};

export const isCrashNoise = (item: TransportItem): boolean => {
  try {
    const strings: string[] = [];
    collectStrings(item.payload, strings);
    const haystack = strings.join("\n");

    if (NOISE_PATTERNS.some((re) => re.test(haystack))) return true;

    if (
      TRANSLATE_REMOVECHILD_PATTERN.test(haystack) &&
      !PDF_SIGNAL_PATTERN.test(haystack)
    ) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

const sanitizeString = (value: string): string =>
  value
    .replace(REDACTED_QUERY_PARAM_PATTERN, "$1$2=[redacted]")
    .replace(EMAIL_PATTERN, "[email]");

const sanitizeDeep = (value: unknown): unknown => {
  if (typeof value === "string") return sanitizeString(value);

  if (Array.isArray(value)) return value.map(sanitizeDeep);

  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) => {
        if (REDACTED_CONTEXT_KEYS.has(key)) return [key, "[redacted]"];

        return [key, sanitizeDeep(val)];
      }),
    );
  }

  return value;
};

const stripUserPii = (meta: TransportItem["meta"]): TransportItem["meta"] => {
  if (!meta.user) return meta;

  const user = Object.fromEntries(
    Object.entries(meta.user).filter(([key]) => !STRIPPED_USER_KEYS.has(key)),
  );

  return { ...meta, user };
};

export const faroBeforeSend = (item: TransportItem): TransportItem | null => {
  try {
    if (isCrashNoise(item)) return null;

    const meta = stripUserPii(item.meta);

    return {
      ...item,
      meta: sanitizeDeep(meta) as TransportItem["meta"],
      payload: sanitizeDeep(item.payload) as TransportItem["payload"],
    };
  } catch {
    return item;
  }
};
